/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React from "react";
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import BASEUtils from "../../../../@BASE/utils/BASEUtils";
import Button from '@mui/material/Button';
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';

import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import _ from '../../../../@lodash';
import { Popover } from '@mui/material';
import BASESvgIcon from "../../../../@BASE/core/BASESvgIcon";
import {
  addReview,
  closeEditReviewDialog,
  closeNewReviewDialog,
  removeReview,
  selectReviewDialog,
  updateReview
} from '../store/reviewsSlice';
import ReviewModel from "../model/ReviewModel";
import { selectFirstCycleId } from "../store/cyclesSlice";
import LessonSelect from "../selects/LessonSelect";
import { formatISO } from 'date-fns';

const defaultValues = ReviewModel();

const schema = yup.object().shape({
  lessons: yup.array().min(1, 'You must select at least one lesson.'),
  cycle: yup.string().required('You must select a cycle.'),
  start: yup.date().required('You must select a start date.'),
  end: yup.date().required('You must select an end date.'),
  pmt_id: yup.string(),
  internality: yup.bool().required('You must select the internality of the review.')
});

function ReviewsDialog(props) {
  const {lessons} = props;
  const dispatch = useDispatch();
  const reviewDialog = useSelector(selectReviewDialog);
  const firstCycleId = useSelector(selectFirstCycleId);

  const { control, formState, watch, reset, getValues } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const { errors, isValid, dirtyFields } = formState;

  const start= watch('start');
  const end= watch('end');
  const id = watch('id');

  const initDialog = useCallback(() => {
    /**
     * Dialog type: 'edit'
     */
    if (reviewDialog.type === 'edit' && reviewDialog.data) {
      reset(reviewDialog.data);
    }

    /**
     * Dialog type: 'new'
     */
    if (reviewDialog.type === 'new') {
      reset({
        ...defaultValues,
        ...reviewDialog.data,
        cycle: 'dev_alpha',
        // id: BASEUtils.generateGUID()
      });
    }
  }, [reviewDialog.data, reviewDialog.type, reset]);
  
  useEffect(() => {
    if (reviewDialog.props.open) {
      initDialog();
    }
  }, [reviewDialog.props.open, initDialog]);


  const closeComposeDialog = () => {
    return reviewDialog.type === 'edit' ? dispatch(closeEditReviewDialog()) : dispatch(closeNewReviewDialog());
  }

  const onSubmit = (ev) => {
    ev.preventDefault();
    const data = getValues();
    if (reviewDialog.type === 'new') {
      dispatch(addReview(data));
    } else {
      dispatch(updateReview(data));
    }
    closeComposeDialog();
  }

  const handleRemove = () => {
    dispatch(removeReview(id));
    closeComposeDialog();
  }

  return (
    <Popover
      {...reviewDialog.props}
      open={reviewDialog.props.open}
      anchorReference="anchorPosition"
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={closeComposeDialog}
      component="form"
    >
      <div className="flex flex-col max-w-full p-24 pt-32 sm:pt-40 sm:p-32 w-480">
        {/* <div className="flex sm:space-x-24 mb-16">
          <Controller
            name="course"
            control={control}
            render={({ field }) => <CourseSelect className="mt-32" courses={courses} {...field} />}
          />
        </div> */}

        <div className="flex sm:space-x-24 mb-16">
          <Controller
            name="lessons"
            control={control}
            render={({ field }) => <LessonSelect lessons={lessons} {...field} />}
          />
        </div>

        <div className="flex sm:space-x-24 mb-16">
          <Controller
            name="cycle"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.cycle}>
                <InputLabel id="select-cycle">Review Cycle</InputLabel>
                <Select
                  {...field}
                  labelId="select-cycle"
                  id="review-cycle"
                  label="Review Cycle"
                  classes={{ select: "flex items-center space-x-12" }}
                  defaultValue=""
                  size="small"
                >
                  <MenuItem value="" disabled className="space-x-12">
                    <span>Select a cycle</span>
                  </MenuItem>
                  <MenuItem value="dev_alpha_internal" className="space-x-12">
                    SB ALPHA Development
                  </MenuItem>
                  <MenuItem value="dev_alpha" className="space-x-12">
                    SB ALPHA Review
                  </MenuItem>
                  <MenuItem value="dev_alpha_revision" className="space-x-12">
                    SB ALPHA Revision
                  </MenuItem>
                  <MenuItem value="dev_beta_internal" className="space-x-12">
                    SB BETA Development
                  </MenuItem>
                  <MenuItem value="dev_beta" className="space-x-12">
                    SB BETA Review
                  </MenuItem>
                  <MenuItem value="dev_beta_revision" className="space-x-12">
                    SB BETA Revision
                  </MenuItem>
                  <MenuItem value="dev_final" className="space-x-12">
                    SB Final Review and Acceptance
                  </MenuItem>
                  <MenuItem value="prod_alpha_internal" className="space-x-12">
                    IMI ALPHA Development
                  </MenuItem>
                  <MenuItem value="prod_alpha" className="space-x-12">
                    IMI ALPHA Review
                  </MenuItem>
                  <MenuItem value="prod_alpha_revision" className="space-x-12">
                    IMI ALPHA Revision
                  </MenuItem>
                  <MenuItem value="prod_beta_internal" className="space-x-12">
                    IMI BETA Development
                  </MenuItem>
                  <MenuItem value="prod_beta" className="space-x-12">
                    IMI BETA Review
                  </MenuItem>
                  <MenuItem value="prod_beta_revision" className="space-x-12">
                    IMI BETA Revision
                  </MenuItem>
                  <MenuItem value="prod_final" className="space-x-12">
                    IMI Final Review and Acceptance
                  </MenuItem>
                  <MenuItem value="trials_pre" className="space-x-12">
                    Pre-Trials QA
                  </MenuItem>
                  <MenuItem value="trials_individual" className="space-x-12">
                    Individual Trials Review
                  </MenuItem>
                  <MenuItem value="trials_individual_revision" className="space-x-12">
                    Individual Trials Revision
                  </MenuItem>
                  <MenuItem value="trials_group" className="space-x-12">
                    Group Trial Review
                  </MenuItem>
                  <MenuItem value="trials_group_revision" className="space-x-12">
                    Group Trial Revision
                  </MenuItem>
                  <MenuItem value="trials_final" className="space-x-12">
                    Trials Final Review and Acceptance
                  </MenuItem>
                </Select>
                {!!errors.cycle ? (
                  <FormHelperText>{errors?.cycle?.message}</FormHelperText>
                ) : null}
              </FormControl>
            )}
          />
        </div>
        
        <div className="flex sm:space-x-24 mb-16">
          <Controller
            name="start"
            control={control}
            render={({ field: { onChange, value } }) => (
              <MobileDatePicker
                value={value}
                onChange={(date) => onChange(date ? formatISO(date, { fractionDigits: 6 }) : null)}
                label="Start Date"
                renderInput={(params) => {
                  const { key, ...rest } = params; // remove key so it isn't spread
                  return (
                    <TextField
                      key={key}
                      label="Start"
                      className="mt-8 mb-16 w-full"
                      size="small"
                      {...rest}
                    />
                  );
                }}
                maxDate={end}
                format="yyyy-MM-dd HH:mm:ss.SSSSSS"
              />
            )}
          />

          <Controller
            name="end"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <MobileDatePicker
                value={value}
                onChange={(date) => onChange(date ? formatISO(date, { fractionDigits: 6 }) : null)}
                label="End Date"
                renderInput={(params) => {
                  const { key, ...rest } = params; // remove key so it isn't spread
                  return (
                    <TextField
                      key={key}
                      label="End"
                      className="mt-8 mb-16 w-full"
                      size="small"
                      {...rest}
                    />
                  );
                }}
                minDate={start}
                format="yyyy-MM-dd HH:mm:ss.SSSSSS"
              />
            )}
          />
        </div>

        <div className="flex sm:space-x-24 mb-16">
          <Controller
            name="pmt_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                className="mt-8 mb-16 w-full"
                id="pmt_id"
                label="Click Up ID"
                type="text"
                error={!!errors.pmt_id}
                helperText={errors?.pmt_id?.message}
                variant="outlined"
                size="small"
              />
            )}
          />

          <Controller
            name="internality"
            control={control}
            render={({ field }) => (
              <FormGroup className="mt-8 mb-16 w-full">
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label="Internal"
                />
              </FormGroup>
            )}
          />
        </div>

        {reviewDialog.type === 'new' ? (
          <div className="flex items-center space-x-8">
            <div className="flex flex-1" />
            <Button
              variant="contained"
              color="secondary"
              onClick={onSubmit}
              disabled={_.isEmpty(dirtyFields) || !isValid}
            >
              Add
            </Button>
          </div>
        ) : (
          <div className="flex items-center space-x-8">
            <div className="flex flex-1" />
            <IconButton onClick={handleRemove} size="large">
              <BASESvgIcon>heroicons-outline:trash</BASESvgIcon>
            </IconButton>
            <Button
              variant="contained"
              color="secondary"
              onClick={onSubmit}
              disabled={_.isEmpty(dirtyFields) || !isValid}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </Popover>
  );
}

export default ReviewsDialog;